/**
 * **输出窗口的派生数据** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 诊断以前只以「节点上的小徽标」出现 —— 得先找到那个节点才知道出了什么问题，
 * 想通览一遍根本做不到。这里把三层信息汇成面板里的一列：
 *
 *   设计器提示 → 刚发生的事，排最前
 *   求值层     → 算不算得出来（未注册求值器 / 抛异常 / 成环）
 *   校验层     → 算出来能不能真的用（空值 / 下游不接受的值）
 *
 * ⚠️ 它是**派生**的，不是攒起来的：图一变，求值 + 校验重算，这里跟着变。
 * 修好的问题自己就消失 —— 之前做成追加式日志时，「历史」里堆的全是
 * 早就修好的旧快照，用户看到十几行，其中大部分是假的。
 *
 * 另外还派生一份 `diagnosticsByNode`（按节点归拢），供节点 header 的徽标用。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * `evalResult` / `validationIssues` / `lastNotice` 都用**闭包**注入 ——
 * 它们在宿主里声明得比这里晚，直接传 ref 会在初始化时读到空值。
 */
import { computed, ref } from 'vue'
import { levelOf } from '@/node/logStore'
import type { Problem } from '@/node/logStore'
import type { DiagnosticSeverity, NodeDiagnostic } from '@/types/node'

export interface ProblemListCtx {
  /** 求值结果（声明在宿主更靠后的位置，所以用闭包） */
  evalResult: () => { diagnostics: readonly NodeDiagnosticLike[] }
  /** 校验问题（同上） */
  validationIssues: () => readonly NodeDiagnosticLike[]
  /** 最后一条设计器提示（同上） */
  lastNotice: () => string
  /**
   * **包报上来的问题**（同上，闭包）。
   *
   * ⚠️ 为什么要有这条：有些问题**只有包自己算得出来** —— 比如打印模板的
   * "这个条码按 X 维度算下来会超出纸面"：它的 IR 在包的编辑器里编译，
   * 内核既不知道 `xDimMicron`，也不知道那台机器的 dpi。
   * 但用户要的是"**统一**在输出窗口里看"（真机反馈），
   * 所以包把结果交上来，这里和求值 / 校验的诊断并成一份列表。
   */
  packageProblems?: () => readonly NodeDiagnosticLike[]
}

/**
 * 只需要 severity / message / nodeId —— 与两套诊断类型都相容。
 *
 * ⚠️ 本地定义，不从 Playground 导 —— composable 不该反向依赖宿主文件。
 * 这里 `severity` 刻意放宽成 `string`（输入侧），到 `NodeDiagnostic` 那一侧
 * 才收窄回 `DiagnosticSeverity`：宿主的两套诊断对 severity 的标注并不一致，
 * 放宽输入才能同时喂进来。
 */
interface NodeDiagnosticLike {
  severity: DiagnosticSeverity
  message: string
  nodeId?: string
}

export function useProblems(ctx: ProblemListCtx) {
  const { evalResult, validationIssues, lastNotice } = ctx

const allDiagnostics = computed(() => [
  ...evalResult().diagnostics,
  ...validationIssues(),
  ...(ctx.packageProblems?.() ?? []),
])

/**
 * 输出窗口。诊断以前只以「节点上的小徽标」出现 —— 得先找到那个节点
 * 才知道出了什么问题，想通览一遍根本做不到。
 */
const logCollapsed = ref(false)

/**
 * 面板里那一列。
 *
 * ⚠️ 它是**派生**的，不是攒起来的：图一变，求值 + 校验重算，
 * 这里跟着变。修好的问题自己就消失 ——
 * 之前做成追加式日志时，「历史」里堆的全是早就修好的旧快照，
 * 用户看到十几行，其中大部分是假的。而且他关心的只有**现在**。
 */
const problems = computed<Problem[]>(() => {
  const toProblem = (
    d: { severity: string; message: string; nodeId?: string },
    source: string,
  ): Problem => ({
    level: levelOf(d.severity),
    source,
    message: d.message,
    ...(d.nodeId ? { nodeId: d.nodeId } : {}),
  })
  const list = [
    // 提示放最前：它是刚发生的事，用户最可能要找它
    ...(lastNotice()
      ? [{ level: 'info' as const, source: '设计器', message: lastNotice() }]
      : []),
    ...evalResult().diagnostics.map((d) => toProblem(d, '求值')),
    ...validationIssues().map((d) => toProblem(d, '校验')),
    /* ⚠️ 来源写"包"而不是"打印"：这一层不该认识具体的包 */
    ...(ctx.packageProblems?.() ?? []).map((d) => toProblem(d, '包')),
  ]
  return list
})

/** 按节点归拢诊断，供 header 徽标使用 */
const diagnosticsByNode = computed(() => {
  const map = new Map<string, NodeDiagnostic[]>()
  for (const d of allDiagnostics.value) {
    if (!d.nodeId) continue
    const list = map.get(d.nodeId)
    const item: NodeDiagnostic = { severity: d.severity, message: d.message }
    if (list) list.push(item)
    else map.set(d.nodeId, [item])
  }
  return map
})

  return { logCollapsed, problems, diagnosticsByNode }
}
